const CHANNEL_NAME = 'vd-recorder-settlements-v1';
const RECORDING_ID = /^[A-Za-z0-9_-]{1,128}$/;
const SETTLEMENT_KEY = /^[A-Za-z0-9_-]{1,128}:[A-Za-z0-9_-]{1,128}$/;
const TERMINAL_INTENT_QUERY_WAIT_MS = 75;

export interface RecorderSettlementSignal {
  publish(recordingId: string): void;
  publishTerminalIntent(recordingId: string, settlementKey: string, active: boolean): void;
  requestTerminalIntents(): Promise<void>;
  subscribe(listener: (recordingId: string) => void): () => void;
  subscribeTerminalIntents(
    listener: (recordingId: string, settlementKey: string, active: boolean) => void,
  ): () => void;
  subscribeTerminalIntentRequests(listener: () => void): () => void;
  dispose(): void;
}

/** Cross-tab invalidation only. Settlement metadata remains in IndexedDB. */
export class BrowserRecorderSettlementSignal implements RecorderSettlementSignal {
  private channel: BroadcastChannel | null = null;
  private readonly listeners = new Set<(recordingId: string) => void>();
  private readonly terminalIntentListeners = new Set<(
    recordingId: string,
    settlementKey: string,
    active: boolean,
  ) => void>();
  private readonly terminalIntentRequestListeners = new Set<() => void>();

  constructor(
    private readonly BroadcastChannelConstructor: typeof BroadcastChannel | undefined
      = globalThis.BroadcastChannel,
  ) {}

  publish(recordingId: string): void {
    if (!RECORDING_ID.test(recordingId)) return;
    this.post({ type: 'settlement-changed', recordingId });
  }

  publishTerminalIntent(recordingId: string, settlementKey: string, active: boolean): void {
    if (!RECORDING_ID.test(recordingId) || !SETTLEMENT_KEY.test(settlementKey)) return;
    this.post({ type: 'terminal-intent', recordingId, settlementKey, active });
  }

  async requestTerminalIntents(): Promise<void> {
    this.post({ type: 'terminal-intent-query' });
    await new Promise((resolve) => setTimeout(resolve, TERMINAL_INTENT_QUERY_WAIT_MS));
  }

  private post(message: Record<string, unknown>): void {
    try {
      this.ensureChannel()?.postMessage(message);
    } catch {
      // A lifecycle cleanup may race publication. Reopen and retry once so the
      // invalidation is not lost during React StrictMode effect replay.
      this.closeChannel();
      try {
        this.ensureChannel()?.postMessage(message);
      } catch {
        this.closeChannel();
      }
    }
  }

  subscribe(listener: (recordingId: string) => void): () => void {
    this.ensureChannel();
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  subscribeTerminalIntents(
    listener: (recordingId: string, settlementKey: string, active: boolean) => void,
  ): () => void {
    this.ensureChannel();
    this.terminalIntentListeners.add(listener);
    return () => this.terminalIntentListeners.delete(listener);
  }

  subscribeTerminalIntentRequests(listener: () => void): () => void {
    this.ensureChannel();
    this.terminalIntentRequestListeners.add(listener);
    return () => this.terminalIntentRequestListeners.delete(listener);
  }

  dispose(): void {
    this.closeChannel();
    this.listeners.clear();
    this.terminalIntentListeners.clear();
    this.terminalIntentRequestListeners.clear();
  }

  private ensureChannel(): BroadcastChannel | null {
    if (this.channel || typeof this.BroadcastChannelConstructor !== 'function') return this.channel;
    this.channel = new this.BroadcastChannelConstructor(CHANNEL_NAME);
    this.channel.addEventListener('message', this.onMessage);
    return this.channel;
  }

  private closeChannel(): void {
    this.channel?.removeEventListener('message', this.onMessage);
    this.channel?.close();
    this.channel = null;
  }

  private onMessage = (event: MessageEvent<unknown>): void => {
    const detail = event.data;
    if (!detail || typeof detail !== 'object') return;
    const { type, recordingId, settlementKey, active } = detail as {
      type?: unknown;
      recordingId?: unknown;
      settlementKey?: unknown;
      active?: unknown;
    };
    if (type === 'terminal-intent-query') {
      this.terminalIntentRequestListeners.forEach((listener) => listener());
      return;
    }
    if (typeof recordingId !== 'string') return;
    if (!RECORDING_ID.test(recordingId)) return;
    if (type === 'settlement-changed') {
      this.listeners.forEach((listener) => listener(recordingId));
      return;
    }
    if (
      type !== 'terminal-intent'
      || typeof settlementKey !== 'string'
      || !SETTLEMENT_KEY.test(settlementKey)
      || typeof active !== 'boolean'
    ) return;
    this.terminalIntentListeners.forEach((listener) => {
      listener(recordingId, settlementKey, active);
    });
  };
}
